/**
 * Query Interface Component
 *
 * Search-bar style input for natural language queries
 * with recent queries dropdown.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Search, Send, Mic, StopCircle, Clock } from 'lucide-react';

const QueryInterface = ({
  onSubmit,
  loading = false,
  placeholder = 'Type your question...',
  disabled = false,
  recentQueries = [],
  onSelectRecent,
}) => {
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const recognitionRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Initialize speech recognition (if available)
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (query.trim() && !loading && !disabled) {
      onSubmit(query.trim());
      setQuery('');
      setShowDropdown(false);
    }
  }, [query, loading, disabled, onSubmit]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleFocus = () => {
    if (recentQueries.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleSelectRecent = (q) => {
    setShowDropdown(false);
    setQuery('');
    if (onSelectRecent) {
      onSelectRecent(q);
    }
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative', width: '100%' }}>
      <form onSubmit={handleSubmit}>
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={loading || disabled}
          autoComplete="off"
          aria-label="Query input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} style={{ color: 'rgba(255,255,255,0.4)' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {/* Voice Input Button (if available) */}
                {recognitionRef.current && (
                  <IconButton
                    size="small"
                    onClick={toggleVoiceInput}
                    disabled={loading || disabled}
                    title={isListening ? 'Stop listening' : 'Voice input'}
                    sx={{
                      color: isListening ? 'error.main' : 'text.secondary',
                      animation: isListening ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                      },
                    }}
                  >
                    {isListening ? <StopCircle size={16} /> : <Mic size={16} />}
                  </IconButton>
                )}
                {/* Submit Button */}
                <IconButton
                  size="small"
                  type="submit"
                  disabled={!query.trim() || loading || disabled}
                  title="Search"
                  sx={{ color: query.trim() ? 'primary.main' : 'text.disabled' }}
                >
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Send size={16} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
        />
      </form>

      {/* Recent Queries Dropdown */}
      {showDropdown && recentQueries.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            zIndex: 1300,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', fontWeight: 600 }}
          >
            Recent Queries
          </Typography>
          <List dense disablePadding>
            {recentQueries.map((q, idx) => (
              <ListItemButton
                key={idx}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur before click registers
                  handleSelectRecent(q);
                }}
                sx={{
                  py: 0.75,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Clock size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={q}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    color: 'text.secondary',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default QueryInterface;
